/**
 * Frontend types for importer-reliability fields on `task.data`.
 * Additive vs `@air/api` until the published package includes them; runtime
 * payloads may already contain these after the backend reliability work.
 */

export type PerItemFailureCategory =
  | 'retryableTransient'
  | 'retryableTimeout'
  | 'terminalPermission'
  | 'terminalBadSourceData'
  | 'unknown';

export type JobTerminationCategory =
  | 'jobTooManyFailures'
  | 'jobCapacity'
  | 'jobSession'
  | 'jobPermission'
  | 'jobTimeout'
  | 'jobResource'
  | 'jobMaxAttempts';

export type ImportFailureReason =
  | 'NO_MORE_STORAGE'
  | 'TOKEN_EXPIRED'
  | 'NO_WORKSPACE_PERMISSION'
  | 'NO_IMPORT_SESSION'
  | 'UNEXPECTED_ERROR'
  | 'PERMISSION_DENIED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT_FAIL'
  | 'TOO_MANY_REQUEST'
  | 'RESOURCE_FAIL'
  | 'GATEWAY_TIMEOUT'
  | 'INCOMPLETE_DATA'
  | 'CORRUPTED_SOURCE_FILE'
  | 'CONNECTION_TIMEOUT'
  | 'ZERO_SIZED_FILE';

export type JobFatalImportFailureReason = Extract<
  ImportFailureReason,
  'NO_MORE_STORAGE' | 'TOKEN_EXPIRED' | 'NO_IMPORT_SESSION' | 'NO_WORKSPACE_PERMISSION'
>;

export type JobTerminationTrigger = 'TIMEOUT' | 'OUT_OF_MEMORY' | 'MAX_ATTEMPTS' | 'TOO_MANY_FAILURES';

/** Matches backend `ImportFailureItem`. */
export type ImportFailureItem = {
  itemType: 'file' | 'folder';
  category: PerItemFailureCategory;
  reason: ImportFailureReason | string;
  sourceName?: string;
  message?: string;
};

/** Matches backend `ImportJobTermination`. */
export type ImportJobTermination = {
  reason: JobFatalImportFailureReason | JobTerminationTrigger | string;
  /** Known categories get tailored copy; unknown ones still show a Stopped row. */
  category: JobTerminationCategory | string;
};

/** Cap mirrors backend `MAX_IMPORT_FAILURES` — detail list only; aggregates are uncapped. */
export const MAX_IMPORT_FAILURE_ITEMS = 1000;

export type ImporterReliabilityData = {
  skippedAlreadyImported?: number;
  skippedExcluded?: number;
  skippedZeroByte?: number;
  skippedUnsupported?: number;
  failureItems?: ImportFailureItem[];
  termination?: ImportJobTermination | null;
  fileImportError?: number;
  folderImportError?: number;
  filesImportedCount?: number;
  foldersImported?: number;
  totalFilesFound?: number;
  totalFoldersFound?: number;
  failures?: Record<string, number>;
};
